/**
 * PageShell — the page contract (UX-8, design-system spine).
 *
 * LOCKED CONTRACT (CTO gate 2026-07-02; the clinical pages compose on
 * this — Security/Data depend on the shape): the access gate is a PROP
 * computed by the page AFTER all its hooks have run. The page body calls
 * its hooks unconditionally (data hooks `enabled`-gated on access), then
 * renders <PageShell access={{ allowed }}>. Because the gate is consumed
 * INSIDE a child component, a page cannot early-return above its hooks —
 * the hooks-before-gate crash class (React #310; took /workspace down in
 * prod) becomes structurally hard to write instead of comment-enforced.
 * Do not move the gate into a hook or an early return, and do not change
 * this signature without a new CTO gate.
 *
 * A new page = PageShell opt-in + primitives: no hand-rolled access gate,
 * container, header, or loading state. Sections with their own queries
 * compose <AsyncBoundary> directly; the `async` prop is for the page's
 * primary query.
 *
 * Forward-watch: with whoami still resolving (null), gates read false and
 * the denied panel shows — parity with the pre-shell pages. If a
 * skeleton-while-resolving treatment is wanted, add an `accessPending`
 * state HERE (one place) rather than per-page.
 */

import { type ReactNode } from 'react';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';
import {
  AsyncBoundary,
  type AsyncBoundaryProps,
} from '@/components/primitives/AsyncBoundary';

export interface PageShellAccess {
  allowed: boolean;
  /** Tenant-neutral denial copy; a neutral default is provided. Its
   *  `{contact_<category>}` placeholders are filled by the shell. */
  deniedMessage?: string;
  /**
   * Rich denied panel override (UX-12) — for pages whose denial surface
   * carries more than a sentence (role display, a back link). Rendered
   * inside the shell body in place of the default panel; the header
   * (eyebrow/title) still comes from the shell.
   */
  deniedContent?: ReactNode;
}

export type PageShellAsync = Omit<AsyncBoundaryProps, 'children'>;

export interface PageShellProps {
  /** Kicker line above the heading (e.g. "Console"). */
  eyebrow?: string;
  /** Page heading; omit for headerless surfaces (e.g. tile dashboards). */
  title?: string;
  subtitle?: string;
  /**
   * The header's action row: the page's primary action and its companions.
   * Pass the actions themselves; the shell lays them out on the title's
   * line when both fit, under it when they do not.
   */
  actions?: ReactNode;
  /**
   * Container width: lg = max-w-4xl (form-width), xl = max-w-6xl
   * (content-width), wide = max-w-7xl (the GC-ruled wide axis — review
   * tables, cockpit-class surfaces), bleed = no container at all (the
   * banded layout class, below).
   *
   * ULTRA-WIDE STEP-UP (founder soak, 2026-07-06): on very wide viewports
   * the fixed caps read ribbon-like, so xl and wide each gain one
   * principled step at the 2xl breakpoint (1536px): xl → max-w-7xl
   * (80rem), wide → max-w-[96rem] (the 2xl breakpoint width itself, so
   * `wide` hugs the viewport at exactly 1536px and caps beyond). lg stays
   * fixed — form columns must not stretch. Running text keeps sane line
   * lengths because the wide surfaces are grids/tables whose columns
   * absorb the extra width.
   *
   * THE BLEED MODE (GC ruling 2026-07-02; rule-of-three trigger FIRED
   * 2026-07-03): full-bleed BANDED surfaces — edge-to-edge color bands
   * with their own inner containers — were held off the shell until a
   * third real instance existed. DirectoryHub (UX-13 S1) is the third,
   * after Hub and the AiTools landing, so `bleed` is the mode designed
   * from those three instances: the shell contributes the access gate,
   * the async boundary, and the <main data-testid> element ONLY; the
   * page owns its bands and their inner containers. Banded surfaces
   * are headerless — the band IS the header — so the header props
   * (eyebrow/title/subtitle/actions) are not rendered in bleed mode,
   * and a denied gate renders in the standard xl container so the
   * denial panel never spans the viewport. Hub and the AiTools landing
   * migrate onto `bleed` opportunistically as their surfaces are next
   * touched, not as forced churn.
   */
  maxWidth?: 'lg' | 'xl' | 'narrow' | 'standard' | 'wide' | 'bleed';
  access?: PageShellAccess;
  /** Async gate for the page's primary query. */
  async?: PageShellAsync;
  /** data-testid for the <main> container. */
  testId?: string;
  children: ReactNode;
}

// DL9: the three page widths are tokens (--container-page-* in
// src/index.css → the max-w-page-* utilities). narrow/standard are the
// semantic names; lg/xl remain as aliases so existing pages need no
// churn (lg ≡ narrow, xl ≡ standard). The 2xl ultra-wide step-ups
// (founder soak 2026-07-06) are unchanged.
const WIDTH_CLASSES: Record<
  Exclude<NonNullable<PageShellProps['maxWidth']>, 'bleed'>,
  string
> = {
  lg: 'max-w-page-narrow',
  narrow: 'max-w-page-narrow',
  xl: 'max-w-page-standard 2xl:max-w-page-wide',
  standard: 'max-w-page-standard 2xl:max-w-page-wide',
  wide: 'max-w-page-wide 2xl:max-w-[96rem]',
};

/** Exported for banded pages whose body containers must match the DL9
 *  widths outside the shell (e.g. DirectoryHub's inner containers). */
export const PAGE_WIDTH_CLASSES = WIDTH_CLASSES;

/** THE page gutter (founder walk 2026-07-06, alignment hotfix): band and
 *  body content columns must share ONE left edge per page, so every
 *  container — the shell's own, SectionBand's inner frame, and the
 *  banded pages' body containers — takes this one horizontal-padding
 *  token. Do not hand-roll px-* on a page-level container. */
export const PAGE_GUTTER = 'px-4';

export function PageShell({
  eyebrow,
  title,
  subtitle,
  actions,
  maxWidth = 'xl',
  access,
  async: asyncProps,
  testId,
  children,
}: PageShellProps): ReactNode {
  // The denied sentence names the admin contact. Cache-only: a page that
  // turns a person away fetches nothing for them.
  const contact = useSupportContact({ enabled: false });
  // Bleed hosts no container of its own; the denied panel still gets the
  // standard xl container so a denial never spans the viewport.
  const width = maxWidth === 'bleed' ? WIDTH_CLASSES.xl : WIDTH_CLASSES[maxWidth];

  const eyebrowEl = eyebrow && (
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sage-400 mb-1">
      {eyebrow}
    </p>
  );

  if (access && !access.allowed) {
    return (
      <main className={`${width} mx-auto ${PAGE_GUTTER} py-6`} data-testid={testId}>
        {eyebrowEl}
        {title && <h1 className="font-display text-2xl text-sage-800 mb-2">{title}</h1>}
        {access.deniedContent ?? (
          <div
            data-testid="page-denied"
            className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-6 text-sm text-ink-soft"
          >
            {contactSentence(
              access.deniedMessage ?? "This page isn't part of your access. Ask {contact_admin} if your work needs it.",
              contact,
            )}
          </div>
        )}
      </main>
    );
  }

  if (maxWidth === 'bleed') {
    return (
      <main data-testid={testId}>
        {asyncProps ? <AsyncBoundary {...asyncProps}>{children}</AsyncBoundary> : children}
      </main>
    );
  }

  return (
    <main className={`${width} mx-auto ${PAGE_GUTTER} py-6 space-y-6`} data-testid={testId}>
      {/* One wrapping row. The title block breaks the line at 12rem, then
          fills it; the action row never shrinks a label and never scrolls:
          when the two do not fit, the actions sit under the title. */}
      {(title || actions) && (
        <header data-testid="page-header" className="flex flex-wrap items-start justify-between gap-4">
          <div data-testid="page-header-title" className="min-w-0 grow basis-48 break-words">
            {eyebrowEl}
            {title && <h1 className="font-display text-2xl text-sage-800">{title}</h1>}
            {subtitle && <p className="text-sm text-ink-soft mt-1">{subtitle}</p>}
          </div>
          {actions && (
            <div data-testid="page-header-actions" className="flex min-w-0 flex-wrap items-center gap-3">
              {actions}
            </div>
          )}
        </header>
      )}
      {asyncProps ? <AsyncBoundary {...asyncProps}>{children}</AsyncBoundary> : children}
    </main>
  );
}
